<template>
  <ValidationObserver ref="observer" tag="div">
    <b-notification
      v-if="mode !== 'rectify' && missingLabels.length"
      type="is-warning"
      :closable="false"
    >
      Pentru inregistrare mai lipsesc:
      <strong>{{ missingLabels.join(", ") }}</strong>
    </b-notification>
    <p class="is-size-7 has-text-grey mb-3">
      * obligatoriu pentru salvarea ciornei; ● necesar la inregistrarea in
      R.E.S.D.P.S.
      <resdps-help-tip :text="requiredMarksHelp" />
    </p>

    <div v-if="mode === 'rectify'" class="box">
      <ValidationProvider
        vid="reason"
        name="Motivul rectificarii"
        rules="required|max:500"
        v-slot="{ errors }"
      >
        <b-field :type="errors[0] ? 'is-danger' : ''" :message="errors[0]">
          <template #label>
            Motivul rectificarii *
            <resdps-help-tip :text="reasonHelp" />
          </template>
          <b-input v-model="reason" type="textarea" maxlength="500" />
        </b-field>
      </ValidationProvider>
    </div>

    <div v-for="section in visibleSections" :key="section.title" class="box">
      <p class="heading">{{ section.title }}</p>
      <div class="columns is-multiline">
        <div
          v-for="field in section.fields"
          :key="field.name"
          :class="['column', field.size]"
        >
          <ValidationProvider
            :vid="field.name"
            :name="field.label"
            :rules="rulesFor(field)"
            v-slot="{ errors }"
          >
            <b-field :type="errors[0] ? 'is-danger' : ''" :message="errors[0]">
              <template #label>
                {{ labelFor(field) }}
                <resdps-help-tip v-if="helpFor(field)" :text="helpFor(field)" />
              </template>
              <b-select
                v-if="field.type === 'origin'"
                v-model="form.origin"
                expanded
              >
                <option
                  v-for="option in originOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </b-select>
              <b-select
                v-else-if="field.type === 'chamber'"
                v-model="form.notaryChamberId"
                expanded
                placeholder="Alegeti camera"
              >
                <option
                  v-for="item in chambers"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.name }}
                </option>
              </b-select>
              <b-datepicker
                v-else-if="field.type === 'date'"
                v-model="form[field.name]"
                locale="ro"
                :min-date="minDateFor(field)"
                :max-date="today"
                placeholder="Alegeti data"
                icon="calendar-today"
              />
              <registry-entry-lookup
                v-else-if="field.type === 'registry'"
                v-model="form.registryEntryId"
                :linked-number="linkedNumber"
                :linked-date="linkedDate"
              />
              <b-input
                v-else-if="field.type === 'textarea'"
                v-model="form[field.name]"
                type="textarea"
                :maxlength="field.max"
              />
              <b-input
                v-else
                v-model="form[field.name]"
                :maxlength="field.max"
              />
            </b-field>
          </ValidationProvider>
        </div>
      </div>
    </div>

    <div class="buttons is-right">
      <b-button :disabled="saving" @click="$emit('cancel')">Renunta</b-button>
      <b-button type="is-primary" :loading="saving" @click="submit">
        {{ submitLabel }}
      </b-button>
    </div>
  </ValidationObserver>
</template>

<script>
import {
  CDPS_BEFORE_DEATH_MESSAGE,
  ORIGIN_LABELS,
  cdpsReceivedBeforeDeath,
  fieldLabel,
  missingForRegistration,
  toFilePayload,
} from "@/helpers/resdps";
import { CONCEPT_HELP, fieldHelp } from "@/helpers/resdpsHelp";
import { notify } from "@/helpers/notify";
import RegistryEntryLookup from "@/components/succession/registry-entry-lookup.vue";
import ResdpsHelpTip from "@/components/succession/resdps-help-tip.vue";

// Sectiunile si campurile (spec E, 5.6). required = obligatoriu la ciorna (*); registration = necesar la
// inregistrare (●), "cdps" doar la cererea C.D.P.S.; chamberRequired = obligatoriu la rectificarea unui dosar transmis.
const SECTIONS = [
  {
    title: "Sesizare",
    fields: [
      {
        name: "origin",
        label: "Tip sesizare",
        type: "origin",
        required: true,
        size: "is-4",
      },
      {
        name: "cdpsReceivedDate",
        label: "Data primirii cererii",
        type: "date",
        registration: "cdps",
        size: "is-4",
      },
    ],
  },
  {
    title: "Defunct",
    fields: [
      {
        name: "deceasedLastName",
        label: "Nume",
        required: true,
        max: 100,
        size: "is-4",
      },
      {
        name: "deceasedFatherInitial",
        label: "Initiala tatalui",
        max: 10,
        size: "is-2",
      },
      {
        name: "deceasedFirstName",
        label: "Prenume",
        required: true,
        max: 100,
        size: "is-6",
      },
      {
        name: "cnp",
        label: "CNP",
        max: 13,
        rules: "cnp",
        registration: true,
        size: "is-4",
      },
      {
        name: "cnpMissingReason",
        label: "Motivul lipsei CNP",
        max: 200,
        registration: true,
        size: "is-8",
      },
    ],
  },
  {
    title: "Ultimul domiciliu",
    fields: [
      {
        name: "domicileLocality",
        label: "Localitate",
        max: 100,
        registration: true,
        size: "is-4",
      },
      {
        name: "domicileCounty",
        label: "Judet",
        max: 100,
        registration: true,
        size: "is-4",
      },
      {
        name: "domicileCua",
        label: "Cod unic de adresa",
        max: 50,
        size: "is-4",
      },
      { name: "domicileStreet", label: "Strada", max: 150, size: "is-6" },
      { name: "domicileNumber", label: "Nr.", max: 20, size: "is-2" },
      { name: "domicileBlock", label: "Bl.", max: 20, size: "is-1" },
      { name: "domicileStaircase", label: "Sc.", max: 20, size: "is-1" },
      { name: "domicileFloor", label: "Et.", max: 20, size: "is-1" },
      { name: "domicileApartment", label: "Ap.", max: 20, size: "is-1" },
    ],
  },
  {
    title: "Deces",
    fields: [
      {
        name: "dateOfDeath",
        label: "Data decesului",
        type: "date",
        required: true,
        size: "is-3",
      },
      {
        name: "deathCertificateNumber",
        label: "Numarul actului de deces",
        max: 50,
        registration: true,
        size: "is-3",
      },
      {
        name: "deathPlaceLocality",
        label: "Localitatea decesului",
        max: 100,
        registration: true,
        size: "is-3",
      },
      {
        name: "deathPlaceCounty",
        label: "Judetul decesului",
        max: 100,
        registration: true,
        size: "is-3",
      },
    ],
  },
  {
    title: "Evidente locale",
    fields: [
      {
        name: "fiscalRollNumber",
        label: "Nr. de rol nominal unic",
        max: 50,
        size: "is-4",
      },
      {
        name: "agriculturalRegisterVolume",
        label: "Registrul agricol - volum",
        max: 20,
        size: "is-4",
      },
      {
        name: "agriculturalRegisterPosition",
        label: "Registrul agricol - pozitie",
        max: 20,
        size: "is-4",
      },
    ],
  },
  {
    title: "Registratura generala",
    fields: [
      {
        name: "registryEntryId",
        label: "Intrarea din registratura generala",
        type: "registry",
        registration: true,
        size: "is-12",
      },
    ],
  },
  {
    title: "Observatii",
    fields: [
      {
        name: "observations",
        label: "Observatii",
        type: "textarea",
        max: 1000,
        size: "is-12",
      },
    ],
  },
  {
    title: "Camera notarilor",
    chamber: true,
    fields: [
      {
        name: "notaryChamberId",
        label: "Camera notarilor",
        type: "chamber",
        chamberRequired: true,
        size: "is-6",
      },
      {
        name: "chamberRegistrationNumber",
        label: "Numar",
        max: 50,
        chamberRequired: true,
        size: "is-3",
      },
      {
        name: "chamberRegistrationDate",
        label: "Data",
        type: "date",
        chamberRequired: true,
        size: "is-3",
      },
    ],
  },
];

export default {
  name: "SuccessionFileFields",
  components: { RegistryEntryLookup, ResdpsHelpTip },
  props: {
    initial: {
      type: Object,
      required: true,
    },
    // add | edit | rectify
    mode: {
      type: String,
      required: true,
    },
    linkedNumber: {
      type: [Number, String],
      default: null,
    },
    linkedDate: {
      type: String,
      default: null,
    },
    showChamber: {
      type: Boolean,
      default: false,
    },
    chamberRequired: {
      type: Boolean,
      default: false,
    },
    chambers: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    today: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      form: { ...this.initial },
      reason: "",
      snapshot: "",
      requiredMarksHelp: CONCEPT_HELP.requiredMarks.text,
      reasonHelp: fieldHelp("reason"),
      originOptions: Object.keys(ORIGIN_LABELS).map((value) => ({
        value,
        label: ORIGIN_LABELS[value],
      })),
    };
  },
  computed: {
    visibleSections() {
      return SECTIONS.filter((section) => !section.chamber || this.showChamber);
    },
    missingLabels() {
      return missingForRegistration(this.form).map(fieldLabel);
    },
    submitLabel() {
      return this.mode === "rectify"
        ? "Salveaza rectificarea"
        : "Salveaza ciorna";
    },
    isDirty() {
      return this.serialize() !== this.snapshot;
    },
  },
  created() {
    this.markSaved();
  },
  methods: {
    serialize() {
      return JSON.stringify({
        form: toFilePayload(this.form, { includeChamber: true }),
        reason: this.reason.trim(),
      });
    },
    markSaved() {
      this.snapshot = this.serialize();
    },
    // Doar campurile din FIELD_HELP primesc iconita de ajutor.
    helpFor(field) {
      return fieldHelp(field.name);
    },
    isRequired(field) {
      return (
        !!field.required || (!!field.chamberRequired && this.chamberRequired)
      );
    },
    labelFor(field) {
      const registration =
        field.registration === "cdps"
          ? this.form.origin === "CdpsRequest"
          : !!field.registration;
      return (
        field.label +
        (this.isRequired(field) ? " *" : "") +
        (registration ? " ●" : "")
      );
    },
    // Data primirii cererii nu poate fi anterioara decesului (regula din API); calendarul o si blocheaza.
    minDateFor(field) {
      return field.name === "cdpsReceivedDate" ? this.form.dateOfDeath : null;
    },
    rulesFor(field) {
      const rules = [];
      if (this.isRequired(field)) rules.push("required");
      if (field.max) rules.push("max:" + field.max);
      if (field.rules) rules.push(field.rules);
      return rules.join("|");
    },
    async submit() {
      const valid = await this.$refs.observer.validate();
      if (!valid) {
        notify.error(this, "Completati campurile marcate.");
        return;
      }
      if (cdpsReceivedBeforeDeath(this.form)) {
        this.setApiErrors(["cdpsReceivedDate"], CDPS_BEFORE_DEATH_MESSAGE);
        notify.error(this, CDPS_BEFORE_DEATH_MESSAGE);
        return;
      }
      // API-ul inlocuieste toate campurile; rectificarea trimite si datele Camerei (null le-ar sterge).
      this.$emit("submit", {
        data: toFilePayload(this.form, {
          includeChamber: this.mode === "rectify",
        }),
        reason: this.reason.trim(),
      });
    },
    // Campurile indicate de API (fields / field) se marcheaza prin vid-ul lor.
    setApiErrors(fields, message) {
      const errors = {};
      fields.forEach((field) => {
        errors[field] = [message];
      });
      if (this.$refs.observer) this.$refs.observer.setErrors(errors);
    },
  },
};
</script>
